import React, { useState, useRef } from "react";
import AsyncSelect from "react-select/async";
import useErrorHandler from "@beeldit/core/errors/useErrorHandler";
import index from "@beeldit/core/services";
import { Pet } from "../models/Pet";

interface Option {
  label: string;
  value: number | string;
}

function CustomAsyncSelectWidget(props: any) {
  const { id, value, required, disabled, readonly, onChange } = props;
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [inputValue, setInputValue] = useState(""); // Estado para manejar la entrada del usuario
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const errorHandler = useErrorHandler();

  // Función de carga con debounce
  const loadOptions = (inputValue: string): Promise<Option[]> => {
    return new Promise((resolve) => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

      debounceTimeout.current = setTimeout(async () => {
        if (!inputValue.trim()) {
          resolve([]); // No buscar si el input está vacío
          return;
        }

        try {
          const response = await index("pets", `search=${inputValue}&relations=customer`);
          const options = response.data.elements.map((pet: Pet) => ({
            label: `${pet.name} (${pet.breed}) - ${pet.customer.name} ${pet.customer.surnames}`,
            value: pet.id,
          }));

          resolve(options);
        } catch (error) {
          errorHandler(error, null);
          resolve([]); // Evitamos que la promesa quede colgada
        }
      }, 1000); // Espera 1 segundo antes de ejecutar la búsqueda
    });
  };

  const handleOnChange = (newValue: Option | null) => {
    setSelectedOption(newValue);
    onChange(newValue?.value ?? null);
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
  };

  const noOptionsMessage = () => {
    return inputValue.trim()
      ? "No se encontraron mascotas"
      : "Escriba el nombre de la mascota, raza o dueño";
  };

  const selectStyles = {
    control: (provided: any) => ({
      ...provided,
      minWidth: "400px",
      borderColor: "#E7F5F2",
      backgroundColor: "#E7F5F2",
      borderRadius: "0.25rem",
    }),
  };

  return (
    <AsyncSelect
      id={id}
      value={selectedOption}
      loadOptions={loadOptions} // Ahora usa debounce correctamente
      onChange={handleOnChange}
      onInputChange={handleInputChange} // Capturamos el input del usuario
      styles={selectStyles}
      placeholder="Escribe para buscar mascotas..."
      isClearable={true}
      cacheOptions
      isDisabled={disabled || readonly}
      noOptionsMessage={noOptionsMessage} // Mensaje personalizado cuando no hay opciones
    />
  );
}

function PetCustomerSelector(props: any) {
  const { id, value, required, disabled, readonly, onChange } = props;
  return (
    <CustomAsyncSelectWidget
      id={id}
      value={value}
      required={required}
      disabled={disabled}
      readonly={readonly}
      onChange={onChange}
    />
  );
}

export default PetCustomerSelector;
